// Shared risk-flag metadata — single source of truth for how risk-intelligence
// flags (the #630 / docs/nc-tax-foreclosure feature set) render across the
// frontend. Mirrors the registry+lookup+fallback discipline of statusMeta.ts:
// one record, one lookup helper, one graceful fallback, a no-orphan-key test.
//
// Three orthogonal axes, each with its own registry:
//   - SEVERITY_META   info | caution | high  → color + label (the chip color)
//   - STATUS_META     detected | possible | not_checked → label + short copy
//   - FLAG_TYPE_META  per-flagType display label + icon glyph
//
// The three-state STATUS is the spine of the product's honesty (see the
// implementation guide §1): we must distinguish "we checked and found this"
// (detected) from "plausible but unverified" (possible) from "we have not
// checked this yet" (not_checked). not_checked is a feature, not a gap — the
// "known unknown" must be visually distinct, never read as "all clear."
//
// Colors are sourced from the index.css :root tokens (light theme only —
// dark mode is retired):
//   high     → --accent-red  (#ef4444)   danger
//   caution  → --accent-amber (#b8791f)  upset-window urgency / caution
//   info     → --accent-blue  (#2e5a88)  neutral brand blue
// Tokens are referenced as CSS var() in App.css; the hex values are duplicated
// here only as a non-DOM fallback (mirroring statusMeta.ts's note), since some
// consumers may need the literal value where a CSS var can't resolve.

// ─── Severity ────────────────────────────────────────────────────────

export type FlagSeverity = "info" | "caution" | "high";

export interface SeverityMeta {
  key: FlagSeverity;
  /** Display label, e.g. "High risk". */
  label: string;
  /** Hex fallback (mirrors the index.css token noted above). */
  color: string;
  /** The CSS custom-property name the chip styling should prefer. */
  cssVar: string;
}

export const SEVERITY_META: Record<FlagSeverity, SeverityMeta> = {
  high: {
    key: "high",
    label: "High risk",
    color: "#ef4444",
    cssVar: "--accent-red",
  },
  caution: {
    key: "caution",
    label: "Caution",
    color: "#b8791f",
    cssVar: "--accent-amber",
  },
  info: {
    key: "info",
    label: "Informational",
    color: "#2e5a88",
    cssVar: "--accent-blue",
  },
};

const UNKNOWN_SEVERITY: SeverityMeta = {
  key: "info",
  label: "Informational",
  color: "#2e5a88",
  cssVar: "--accent-blue",
};

/**
 * Look up severity metadata. Always returns a SeverityMeta (unknown severities
 * fall back to the neutral "info" treatment) so callers never null-check.
 */
export function severityMeta(key: string | null | undefined): SeverityMeta {
  if (!key) return UNKNOWN_SEVERITY;
  return SEVERITY_META[key as FlagSeverity] ?? UNKNOWN_SEVERITY;
}

// ─── Status (the three-state spine) ───────────────────────────────────

export type FlagStatus = "detected" | "possible" | "not_checked";

export interface StatusMeta {
  key: FlagStatus;
  /** Badge label, e.g. "Detected" / "Possible" / "Not checked". */
  label: string;
  /** Short clarifying copy for a tooltip / sub-line. */
  short: string;
}

export const STATUS_META: Record<FlagStatus, StatusMeta> = {
  detected: {
    key: "detected",
    label: "Detected",
    short: "We checked and found this.",
  },
  possible: {
    key: "possible",
    label: "Possible",
    short: "Plausible from the data, but unverified — confirm before relying on it.",
  },
  not_checked: {
    key: "not_checked",
    label: "Not checked",
    short:
      "We have not verified this yet — a known unknown, not an all-clear. Diligence it yourself.",
  },
};

const UNKNOWN_STATUS: StatusMeta = {
  key: "not_checked",
  label: "Not checked",
  short:
    "We have not verified this yet — a known unknown, not an all-clear. Diligence it yourself.",
};

/**
 * Look up status metadata. Always returns a StatusMeta; unknown values fall
 * back to "Not checked" (the honest default — never imply "clear").
 */
export function statusFlagMeta(key: string | null | undefined): StatusMeta {
  if (!key) return UNKNOWN_STATUS;
  return STATUS_META[key as FlagStatus] ?? UNKNOWN_STATUS;
}

// ─── Flag type (per-concern display label + icon) ─────────────────────

export interface FlagTypeMeta {
  key: string;
  /** Display label for the chip title, e.g. "Federal tax lien". */
  label: string;
  /** A dependency-light glyph (plain unicode — no icon-font dependency). */
  icon: string;
}

/**
 * Master registry of known flag types. The backend's known flagTypes today are
 * `federal_tax_lien`, `state_tax_lien`, `subsequent_year_taxes`, `title_insurability`,
 * `lien_survival` and `code_violation`. Add new
 * flag types HERE — riskFlagMeta.test.ts pins the no-orphan-key contract via
 * the UNKNOWN fallback.
 */
export const FLAG_TYPE_META: Record<string, FlagTypeMeta> = {
  federal_tax_lien: {
    key: "federal_tax_lien",
    label: "Federal tax lien",
    icon: "⚑",
  },
  state_tax_lien: {
    key: "state_tax_lien",
    label: "NC tax lien",
    icon: "⚑",
  },
  subsequent_year_taxes: {
    key: "subsequent_year_taxes",
    label: "Subsequent-year taxes",
    icon: "$",
  },
  title_insurability: {
    key: "title_insurability",
    label: "Title insurability",
    icon: "§",
  },
  // Forward-looking entries for the flags the implementation guide sequences
  // next, so they render with a real label the moment the backend emits them.
  foreclosure_method: {
    key: "foreclosure_method",
    label: "Foreclosure method",
    icon: "⚖",
  },
  unknown_heirs_due_process: {
    key: "unknown_heirs_due_process",
    label: "Due process / notice",
    icon: "!",
  },
  demolition_lien: {
    key: "demolition_lien",
    label: "Demolition lien",
    icon: "⚑",
  },
  nuisance_lien: {
    key: "nuisance_lien",
    label: "Nuisance lien",
    icon: "⚑",
  },
  // Emitted by the backend's codeViolation evaluator for every property:
  // detected/possible where we read a municipal code-enforcement feed,
  // not_checked everywhere else. See internal/riskflags/evaluators.go.
  code_violation: {
    key: "code_violation",
    label: "Code enforcement",
    icon: "⌂",
  },
};

const UNKNOWN_FLAG_TYPE: FlagTypeMeta = {
  key: "unknown",
  label: "Risk flag",
  icon: "•",
};

/**
 * Look up display metadata for a flag type. Always returns a FlagTypeMeta —
 * unknown keys get a neutral "Risk flag" label so a new backend flag type
 * still renders (graceful, never crashes). If a real flag type hits this
 * fallback, ADD it to FLAG_TYPE_META.
 *
 * @example
 * riskFlagMeta("federal_tax_lien").label  // "Federal tax lien"
 * riskFlagMeta("made_up").label           // "Risk flag"
 */
export function riskFlagMeta(key: string | null | undefined): FlagTypeMeta {
  if (!key) return UNKNOWN_FLAG_TYPE;
  return FLAG_TYPE_META[key] ?? UNKNOWN_FLAG_TYPE;
}
